import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Checkbox, Prompt } from 'sonata-react-components';

import './noUpliftConfirmPrompt.scss';

type Props = {
  selectedCount: number;
  noUpliftCount: number;
  onCancel: () => void;
  onConfirm: (discardNoUplift: boolean) => void;
};

type State = {
  discardNoUplift: boolean;
};

export function NoUpliftConfirmPrompt({ selectedCount, noUpliftCount, onCancel, onConfirm }: Props) {
  const [{ discardNoUplift }, setState] = useState<State>({ discardNoUplift: false });
  const { formatMessage } = useIntl();

  return (
    <Prompt className="noUpliftConfirmPrompt" closePrompt={onCancel} width="770px">
      <div className="noUpliftConfirmPrompt__iconWrapper">
        <i className="fal fa-triangle-exclamation noUpliftConfirmPrompt__icon" />
      </div>
      <Prompt.Title className="noUpliftConfirmPrompt__title" variant="warning">
        {formatMessage({
          defaultMessage: 'Some POIs do not have uplift information',
          id: 'driveToStore.noUpliftPrompt.title'
        })}
      </Prompt.Title>
      <Prompt.Description className="noUpliftConfirmPrompt__description">
        {formatMessage({
          defaultMessage: 'Keep in mind that charts will only show partial data for POIs with no uplift.',
          id: 'driveToStore.noUpliftPrompt.description'
        })}
      </Prompt.Description>
      <div className="noUpliftConfirmPrompt__stats">
        <div className="noUpliftConfirmPrompt__statRow">
          <span className="noUpliftConfirmPrompt__statLabel">
            {formatMessage({ defaultMessage: 'Selected POIs:', id: 'driveToStore.noUpliftPrompt.selectedPois' })}
          </span>
          <span className="noUpliftConfirmPrompt__statValue">{selectedCount} POIs</span>
        </div>
        <div className="noUpliftConfirmPrompt__statRow">
          <span className="noUpliftConfirmPrompt__statLabel">
            {formatMessage({ defaultMessage: 'POIs with no uplift:', id: 'driveToStore.noUpliftPrompt.noUpliftPois' })}
          </span>
          <span className="noUpliftConfirmPrompt__statValue noUpliftConfirmPrompt__statValue--warning">
            {noUpliftCount} POIs
          </span>
        </div>
      </div>
      <div className="noUpliftConfirmPrompt__checkbox">
        <Checkbox
          active={discardNoUplift}
          id="discard-no-uplift-checkbox"
          labelText={formatMessage({
            defaultMessage: 'Discard POIs with no uplift',
            id: 'driveToStore.noUpliftPrompt.discardCheckbox'
          })}
          onClickAction={() => setState(prev => ({ ...prev, discardNoUplift: !prev.discardNoUplift }))}
          type="checkbox"
        />
      </div>
      <Prompt.Buttons position="centered">
        <ButtonUI
          id="no-uplift-cancel"
          onClick={onCancel}
          shape="squareRounded"
          text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
          type="secondary"
        />
        <ButtonUI
          id="no-uplift-confirm"
          onClick={() => onConfirm(discardNoUplift)}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: 'Accept & See aggregated report →',
            id: 'driveToStore.noUpliftPrompt.confirm'
          })}
          type="primary"
        />
      </Prompt.Buttons>
    </Prompt>
  );
}
